import React, { useMemo, useState } from 'react';
import { Col, Modal, Row, Spin } from 'antd';
import {
    CubeIcon,
    CurrencyDollarIcon,
    HeartIcon,
    SquaresFourIcon,
    TreeEvergreenIcon,
} from '@phosphor-icons/react';
import type { SelectedDesignForPricing } from '../../../../../store/oneButtonStore';
import { isLayoutProductionOverAnnualUsage, resolveLayoutRoofCount } from '../utils/layout';
import { resolveCatalogCardSnapshotUrl, resolveDefaultFallbackSnapshotUrl } from '../utils/catalogCardSnapshot';
import type { AiDesignsDataSource } from './DashboardAiDesignsSourceBadge';
import type { CategoryLayoutCandidate } from '../utils/categoryBestVariantScoring';
import { useViewerStore } from '../../../../../store/store';
import { STABLE_EMPTY_OBJECT } from '../utils/stableEmpty';
import '../oneButton-css/OneButtonFormControls.css';

/** Matches `DashboardAiLayoutCardView` / `DashboardAiSlideDesignsList` row ids. */
function getCatalogRowCardKey(categoryLabel: string, layoutKey: string): string {
    return `${categoryLabel}-${layoutKey}`;
}

function isCatalogRowPricingSelected(
    categoryLabel: string,
    layoutKey: string,
    selected: SelectedDesignForPricing | null | undefined,
    trigger: SelectedDesignForPricing | null | undefined,
): boolean {
    const cardKey = getCatalogRowCardKey(categoryLabel, layoutKey);
    const ids = [selected?.designId, trigger?.designId].filter(Boolean) as string[];
    return ids.some((id) => id === cardKey || id === layoutKey);
}

function hasCatalogVariantPricingSelected(
    categoryLabel: string,
    layoutKey: string,
    selected: SelectedDesignForPricing | null | undefined,
): boolean {
    if (!selected) return false;
    const cardKey = getCatalogRowCardKey(categoryLabel, layoutKey);
    if (selected.designId === cardKey || selected.designId === layoutKey) return false;
    return selected.categoryId === categoryLabel && selected.designId.startsWith(`${categoryLabel}:`);
}

export type DashboardAiLayoutTableViewProps = {
    theme: 'dark' | 'light';
    layoutCategoryNames: string[];
    /** Same candidate keys as `DashboardAiLayoutCardView` (per-category primary layout). */
    layoutCandidatesByCategory: Record<string, unknown>;
    layoutMetricsByKey: Record<string, unknown>;
    renderMainCardPartnerValue: (cardKey: string, partner: 'GoodLeap' | 'IGS' | 'LightReach') => React.ReactNode;
    getSelectedLayoutTitle: () => string;
    totalModulesBestRoofs: number | null | undefined;
    /** All scene roof face ids for oneDRAW Default Maxfill (not the active selection). */
    defaultMaxfillRoofIds: string[];
    activeLayoutRoofIds: string[];
    activeLayoutOverUsage: boolean;
    simulatingLayouts: boolean;
    layoutProductionByKey: Record<string, number | undefined>;
    selectedRoofCombinationKey: string;
    setSelectedRoofCombinationKey: (key: string) => void;
    activeLayoutProduction: number;
    activeModule: unknown;
    roofCards: any[];
    treeCount: number;
    bestRoofsForLayout: { fromAi?: boolean };
    roofCombinationsFromAi: any[];
    layoutModulesByKey: Record<string, number | undefined>;
    annualUsageCapKwh: number | null | undefined;
    expectedCountByRoofId: Record<string, number | undefined>;
    selectedDesignForPricing: SelectedDesignForPricing | null;
    pricingDesignForTrigger: SelectedDesignForPricing | null;
    pricingSelectionLoading: boolean;
    savedAIDesigns: any[];
    handleSelectLayoutCard: (key: string, details: any) => void;
    handlePricingDollarClick: (key: string, details: any) => void;
    handleApplyLayoutClick: (layoutKey: string, categoryLabel?: string) => void | Promise<void>;
    handleApplyAdHocLayout: (roofIds: string[]) => Promise<void>;
    handleWishlistDesign: (
        key: string,
        categoryId: string,
        systemSizeKwOverride?: number | null,
        roofCountOverride?: number | null,
    ) => void;
    dashboardSystemSizeKw?: number | null;
    treeScenariosByLayoutKey: Record<string, any[] | undefined>;
    setTreeCuttingForLayoutKey: (key: string) => void;
    setTreeCuttingPopupOpen: (open: boolean) => void;
    setTreeCuttingConfirmForKey: (key: string | null) => void;
    setTreeCuttingConfirmOpen: (open: boolean) => void;
    layoutSnapshotsByKey?: Record<string, string | undefined>;
    useCatalogInlineSubpanels?: boolean;
    onCatalogTreeSubpanelOpen?: (payload: {
        layoutKey: string;
        designLabel: string;
        mainSnapshotUrl?: string;
        needsConfirm: boolean;
    }) => void;
    onOpenCategoryLayoutOptions?: (payload: {
        categoryLabel: string;
        layoutKey: string;
        designLabel?: string;
        mainSnapshotUrl?: string;
        useSubpanel?: boolean;
    }) => void | Promise<void>;
    setAltLayoutsCategory: (label: string | null) => void;
    setAltLayoutsLoading: (loading: boolean) => void;
    setAltLayoutsModalOpen: (open: boolean) => void;
    aiCategoryLayoutVariants: Record<string, unknown[]>;
};

/** Column flex bases — parity with former <colgroup> widths (sums to 100%). */
const AI_CATALOG_COL_FLEX = ['8%', '17%', '5%', '6%', '8%', '12%', '10%', '10%', '12%', '12%'] as const;

const AI_CATALOG_HEADER_LABELS = [
    'Image',
    'Design',
    'Roofs',
    'Modules',
    'System',
    'Est. production',
    'Stats',
    'Status',
    'Pricing',
    'Action',
] as const;

type CatalogGridColProps = {
    flexPct: string;
    head?: boolean;
    children: React.ReactNode;
};

const CatalogGridCol: React.FC<CatalogGridColProps> = ({ flexPct, head, children }) => (
    <Col
        flex={`0 0 ${flexPct}`}
        style={{ maxWidth: flexPct, minWidth: 0 }}
        className={['editor-ai-roofs-table__cell', head ? 'editor-ai-roofs-table__cell--head' : 'editor-ai-roofs-table__cell--body'].join(
            ' ',
        )}
    >
        {children}
    </Col>
);

function formatTableSystemKw(mods: number | null | undefined, activeModule: unknown): string {
    const pw = (activeModule as any)?.power ?? 0;
    return mods != null && pw > 0 ? ((mods * pw) / 1000).toFixed(2) : '—';
}

/** Same thumbnail resolution order as {@link DashboardAiLayoutCardView}. */
function resolveCatalogRowSnapshotUrl(
    layoutKey: string,
    categoryLabel: string,
    layoutSnapshotsByKey: Record<string, string | undefined> | undefined,
    aiCategoryLayoutVariants: Record<string, unknown[]>,
    categoryMainLayoutByCategory: Record<string, CategoryLayoutCandidate>,
    aiLayoutVariantSnapshots: Record<string, string>,
    defaultFallbackSnapshotUrl?: string,
    aiDesignsDataSource?: AiDesignsDataSource | null,
    layoutHasMetrics?: boolean,
): string | undefined {
    return resolveCatalogCardSnapshotUrl({
        layoutKey,
        categoryLabel,
        layoutSnapshotsByKey,
        categoryMainLayoutByCategory,
        aiCategoryLayoutVariants,
        aiLayoutVariantSnapshots,
        defaultFallbackSnapshotUrl,
        aiDesignsDataSource,
        layoutHasMetrics,
    });
}

export const DashboardAiLayoutTableView: React.FC<DashboardAiLayoutTableViewProps> = ({
    theme,
    layoutCategoryNames,
    layoutCandidatesByCategory,
    layoutMetricsByKey,
    renderMainCardPartnerValue,
    totalModulesBestRoofs,
    defaultMaxfillRoofIds,
    activeLayoutOverUsage,
    simulatingLayouts,
    layoutProductionByKey,
    selectedRoofCombinationKey,
    setSelectedRoofCombinationKey,
    activeLayoutProduction,
    activeModule,
    roofCards,
    treeCount,
    bestRoofsForLayout,
    roofCombinationsFromAi,
    layoutModulesByKey,
    annualUsageCapKwh,
    activeLayoutRoofIds,
    selectedDesignForPricing,
    pricingDesignForTrigger,
    pricingSelectionLoading,
    savedAIDesigns,
    handleSelectLayoutCard,
    handlePricingDollarClick,
    handleApplyLayoutClick,
    handleApplyAdHocLayout,
    handleWishlistDesign,
    dashboardSystemSizeKw,
    treeScenariosByLayoutKey,
    setTreeCuttingForLayoutKey,
    setTreeCuttingPopupOpen,
    setTreeCuttingConfirmForKey,
    setTreeCuttingConfirmOpen,
    layoutSnapshotsByKey,
    useCatalogInlineSubpanels = false,
    onCatalogTreeSubpanelOpen,
    onOpenCategoryLayoutOptions,
    setAltLayoutsCategory,
    setAltLayoutsLoading,
    setAltLayoutsModalOpen,
    aiCategoryLayoutVariants,
}) => {
    const numberColor = theme === 'light' ? '#1e293b' : 'rgba(248,250,252,0.96)';
    const [snapshotPreview, setSnapshotPreview] = useState<{ label: string; url: string } | null>(null);
    const categoryMainLayoutByCategory = useViewerStore(
        (s) =>
            (s.categoryMainLayoutByCategory ?? STABLE_EMPTY_OBJECT) as Record<string, CategoryLayoutCandidate>,
    );
    const aiDesignsDataSource = useViewerStore(
        (s: any) => (s.aiDesignsDataSource as AiDesignsDataSource | undefined) ?? null,
    );
    const aiLayoutVariantSnapshots = useViewerStore(
        (s: any) => (s.aiLayoutVariantSnapshots ?? STABLE_EMPTY_OBJECT) as Record<string, string>,
    );
    const layoutRoofSummariesByKey = useViewerStore(
        (s) => (s.layoutRoofSummariesByKey ?? STABLE_EMPTY_OBJECT) as Record<string, unknown[]>,
    );
    const defaultRoofIds = useMemo(() => {
        if ((defaultMaxfillRoofIds?.length ?? 0) > 0) {
            return defaultMaxfillRoofIds;
        }
        return (roofCards || []).map((r: any) => r.roofFaceId).filter(Boolean) as string[];
    }, [defaultMaxfillRoofIds, roofCards]);

    const fallbackSnapshotUrl = useMemo(
        () => resolveDefaultFallbackSnapshotUrl(layoutSnapshotsByKey, aiDesignsDataSource),
        [layoutSnapshotsByKey, aiDesignsDataSource],
    );

    const rows = useMemo(() => {
        const out: { key: string; label: string; roofFaceIds: string[]; isCategoryPlaceholder: boolean }[] = [
            {
                key: 'default',
                label: 'oneDRAW Default Maxfill',
                roofFaceIds: defaultRoofIds,
                isCategoryPlaceholder: false,
            },
        ];
        for (const cat of layoutCategoryNames || []) {
            const first = (layoutCandidatesByCategory as any)?.[cat]?.[0] as { key?: string } | undefined;
            if (!first?.key) continue;
            const combo = (roofCombinationsFromAi as any[])?.find((c: any) => c.name === first.key);
            out.push({
                key: first.key,
                label: cat,
                roofFaceIds: Array.isArray(combo?.roofFaceIds) ? combo.roofFaceIds : [],
                isCategoryPlaceholder: false,
            });
        }
        return out;
    }, [layoutCategoryNames, layoutCandidatesByCategory, roofCombinationsFromAi, defaultRoofIds]);

    return (
        <div
            className="editor-ai-layout-detail-card editor-ai-layout-detail-card--catalog-table"
            style={{ display: 'flex', flexDirection: 'column', minHeight: 0, height: '100%', flex: 1 }}
        >
            <div className="editor-ai-roofs-table-shell">
                <div className="editor-ai-roofs-table editor-ai-roofs-table--head editor-ai-roofs-table--grid">
                    <Row className="editor-ai-roofs-table__head-row" gutter={0} wrap={false}>
                        {AI_CATALOG_HEADER_LABELS.map((label, i) => (
                            <CatalogGridCol key={label} flexPct={AI_CATALOG_COL_FLEX[i]} head>
                                <span role="columnheader">{label}</span>
                            </CatalogGridCol>
                        ))}
                    </Row>
                </div>
                <div className="editor-ai-roofs-table-body-scroll">
                    <div className="editor-ai-roofs-table editor-ai-roofs-table--body editor-ai-roofs-table--grid">
                        {rows.map((row: any, idx: number) => {
                            const cardKey = getCatalogRowCardKey(row.label, row.key);
                            const isSelected = isCatalogRowPricingSelected(
                                row.label,
                                row.key,
                                selectedDesignForPricing,
                                pricingDesignForTrigger,
                            );
                            const hasVariantSelected = hasCatalogVariantPricingSelected(
                                row.label,
                                row.key,
                                selectedDesignForPricing,
                            );
                            const isRowSelected = isSelected || hasVariantSelected;
                            const estProd = layoutProductionByKey[row.key];
                            const estModules =
                                row.key === 'default' && totalModulesBestRoofs != null
                                    ? totalModulesBestRoofs
                                    : layoutModulesByKey[row.key];
                            const isSimulatingThis =
                                !row.isCategoryPlaceholder && simulatingLayouts && (estProd === undefined || estModules === undefined);
                            const isOverUsage =
                                !isSimulatingThis && isLayoutProductionOverAnnualUsage(estProd, annualUsageCapKwh ?? null);
                            const systemKw = formatTableSystemKw(estModules ?? null, activeModule);
                            const metrics = (layoutMetricsByKey as any)?.[row.key] as
                                | {
                                      efficiency?: number | null;
                                      solarAccess?: number | null;
                                      multiplier?: number | null;
                                      roofCount?: number | null;
                                  }
                                | undefined;
                            const effText =
                                metrics?.efficiency != null && Number.isFinite(metrics.efficiency)
                                    ? `${Math.round(metrics.efficiency)}%`
                                    : '—';
                            const solarText =
                                metrics?.solarAccess != null && Number.isFinite(metrics.solarAccess)
                                    ? `${Number(metrics.solarAccess).toFixed(1)}%`
                                    : '—';
                            const multText =
                                metrics?.multiplier != null && Number.isFinite(metrics.multiplier)
                                    ? `x${Number(metrics.multiplier).toFixed(2)}`
                                    : '—';
                            const systemSizeKwValue =
                                estModules != null && Number.isFinite(estModules) && Number((activeModule as any)?.power) > 0
                                    ? (Number(estModules) * Number((activeModule as any)?.power)) / 1000
                                    : undefined;
                            const pricingBusy =
                                pricingSelectionLoading &&
                                isCatalogRowPricingSelected(
                                    row.label,
                                    row.key,
                                    selectedDesignForPricing,
                                    pricingDesignForTrigger,
                                );
                            const wishlisted = savedAIDesigns.some(
                                (d: any) => d.layoutKey === row.key && d.categoryId === row.label,
                            );
                            const rowRoofCount = resolveLayoutRoofCount({
                                layoutKey: row.key,
                                layoutMetricsByKey: layoutMetricsByKey as Record<string, { roofCount?: number | null }>,
                                defaultMaxfillRoofIds,
                                roofCards,
                                roofCombinationsFromAi,
                                categoryMainLayoutByCategory,
                                layoutRoofSummariesByKey,
                            });
                            const rowModules = layoutModulesByKey[row.key];
                            const rowProduction = layoutProductionByKey[row.key];
                            const thumbSnapshot = resolveCatalogRowSnapshotUrl(
                                row.key,
                                row.label,
                                layoutSnapshotsByKey,
                                aiCategoryLayoutVariants,
                                categoryMainLayoutByCategory,
                                aiLayoutVariantSnapshots,
                                fallbackSnapshotUrl,
                                aiDesignsDataSource,
                                (rowModules != null && Number.isFinite(rowModules) && rowModules > 0) ||
                                    (rowProduction != null && Number.isFinite(rowProduction) && rowProduction > 0),
                            );
                            return (
                                <Row
                                style={{
                                    borderBottom: '1px solid rgba(148,163,184,0.35)',
                                }}
                                    key={cardKey}
                                    className={['editor-ai-roofs-table__row', isRowSelected ? 'editor-ai-roofs-table__row--selected' : '']
                                        .filter(Boolean)
                                        .join(' ')}
                                    gutter={0}
                                    wrap={false}
                                >
                                    <CatalogGridCol flexPct={AI_CATALOG_COL_FLEX[0]}>
                                        {thumbSnapshot ? (
                                            <button
                                                type="button"
                                                onClick={() =>
                                                    setSnapshotPreview({
                                                        label: row.label,
                                                        url: thumbSnapshot,
                                                    })
                                                }
                                                aria-label={`View full layout image: ${row.label}`}
                                                title="View full image"
                                                style={{
                                                    width: 60,
                                                    height: 40,
                                                    borderRadius: 6,
                                                    border: '1px solid rgba(148,163,184,0.35)',
                                                    overflow: 'hidden',
                                                    padding: 0,
                                                    background: 'transparent',
                                                    cursor: 'zoom-in',
                                                    display: 'inline-flex',
                                                    alignItems: 'center',
                                                    justifyContent: 'center',
                                                }}
                                            >
                                                <img
                                                    src={thumbSnapshot}
                                                    alt=""
                                                    style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
                                                />
                                            </button>
                                        ) : (
                                            <span style={{ color: 'rgba(148,163,184,0.9)' }}>—</span>
                                        )}
                                    </CatalogGridCol>
                                    <CatalogGridCol flexPct={AI_CATALOG_COL_FLEX[1]}>
                                        <div className="editor-ai-roofs-table__design-cell">
                                            {isSimulatingThis ? (
                                                <span>{row.label}</span>
                                            ) : row.isCategoryPlaceholder ? (
                                                <span>{row.label}</span>
                                            ) : (
                                                <>
                                                    <div className="editor-ai-roofs-table__design-title">
                                                        <button
                                                            type="button"
                                                            className={`obd-ai-catalog-list__name${isRowSelected ? ' obd-ai-catalog-list__name--selected' : ''}`}
                                                            onClick={() => {
                                                                if (isOverUsage || isSimulatingThis) return;
                                                                setSelectedRoofCombinationKey(row.key);
                                                                if (!isRowSelected) {
                                                                    handleSelectLayoutCard(row.key, {
                                                                        estProd,
                                                                        estModules,
                                                                        systemKw,
                                                                        systemSizeKw: systemSizeKwValue,
                                                                        roofCount: rowRoofCount,
                                                                        categoryId: row.label,
                                                                        designId: cardKey,
                                                                    });
                                                                }
                                                                void handleApplyLayoutClick(
                                                                    row.key === 'default' ? 'default' : row.key,
                                                                    row.key === 'default' ? undefined : row.label,
                                                                );
                                                            }}
                                                            style={{ background: 'none', border: 0, padding: 0, textAlign: 'left' }}
                                                        >
                                                            {row.label}
                                                        </button>
                                                    </div>
                                                    {isRowSelected ? (
                                                        <div className="editor-ai-roofs-table__design-chip-row">
                                                            <span className="editor-ai-design-card__badge editor-ai-design-card__badge--inline">
                                                                Selected
                                                            </span>
                                                        </div>
                                                    ) : null}
                                                </>
                                            )}
                                        </div>
                                    </CatalogGridCol>
                                    <CatalogGridCol flexPct={AI_CATALOG_COL_FLEX[2]}>
                                        {rowRoofCount}
                                    </CatalogGridCol>
                                    <CatalogGridCol flexPct={AI_CATALOG_COL_FLEX[3]}>
                                        {isSimulatingThis ? 'Simulating…' : (estModules ?? '—')}
                                    </CatalogGridCol>
                                    <CatalogGridCol flexPct={AI_CATALOG_COL_FLEX[4]}>
                                        {isSimulatingThis ? 'Simulating…' : `${systemKw} kW`}
                                    </CatalogGridCol>
                                    <CatalogGridCol flexPct={AI_CATALOG_COL_FLEX[5]}>
                                        {isSimulatingThis ? (
                                            'Simulating…'
                                        ) : (
                                            <span
                                                style={{
                                                    color: isOverUsage ? '#fb7185' : numberColor,
                                                    fontWeight: isOverUsage ? 700 : undefined,
                                                }}
                                            >
                                                {typeof estProd === 'number'
                                                    ? `${Math.round(estProd).toLocaleString()} kWh/yr`
                                                    : '—'}
                                            </span>
                                        )}
                                    </CatalogGridCol>
                                    <CatalogGridCol flexPct={AI_CATALOG_COL_FLEX[6]}>
                                        {row.isCategoryPlaceholder ? (
                                            '—'
                                        ) : isSimulatingThis ? (
                                            'Simulating…'
                                        ) : (
                                            <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                                                <span style={{ fontSize: 11, color: theme === 'light' ? '#334155' : 'rgba(203,213,225,0.95)' }}>
                                                    <b>Solar Access:</b>{solarText}
                                                </span>
                                                <span style={{ fontSize: 11, color: theme === 'light' ? '#334155' : 'rgba(203,213,225,0.95)' }}>
                                                    <b>Efficiency:</b> {effText}
                                                </span>
                                                <span style={{ fontSize: 11, color: theme === 'light' ? '#334155' : 'rgba(203,213,225,0.95)' }}>
                                                    <b>Multiplier:</b>{multText}
                                                </span>
                                            </div>
                                        )}
                                    </CatalogGridCol>
                                    <CatalogGridCol flexPct={AI_CATALOG_COL_FLEX[7]}>
                                        <span>
                                            {row.isCategoryPlaceholder
                                                ? 'No designs yet'
                                                : isOverUsage
                                                  ? 'Not sellable'
                                                  : isRowSelected
                                                    ? 'Active'
                                                    : row.key === 'default'
                                                      ? 'Default'
                                                      : 'Ready'}
                                        </span>
                                    </CatalogGridCol>
                                    <CatalogGridCol flexPct={AI_CATALOG_COL_FLEX[8]}>
                                        {row.isCategoryPlaceholder ? (
                                            '—'
                                        ) : (
                                            <div style={{ display: 'flex', flexDirection: 'column', gap: 2, fontSize: 11 }}>
                                                <span style={{ color: theme === 'light' ? '#334155' : 'rgba(203,213,225,0.95)' }}>
                                                    GL: {renderMainCardPartnerValue(cardKey, 'GoodLeap')}
                                                </span>
                                                <span style={{ color: theme === 'light' ? '#334155' : 'rgba(203,213,225,0.95)' }}>
                                                    IGS: {renderMainCardPartnerValue(cardKey, 'IGS')}
                                                </span>
                                                <span style={{ color: theme === 'light' ? '#334155' : 'rgba(203,213,225,0.95)' }}>
                                                    LR: {renderMainCardPartnerValue(cardKey, 'LightReach')}
                                                </span>
                                            </div>
                                        )}
                                    </CatalogGridCol>
                                    <CatalogGridCol flexPct={AI_CATALOG_COL_FLEX[9]}>
                                        {row.isCategoryPlaceholder ? (
                                            <div className="obd-ai-catalog-list__actions obd-ai-catalog-list__actions--dashboard-grid">
                                                <button
                                                    type="button"
                                                    className="obd-ai-slide-designs__3d-btn obd-ai-catalog-list__icon-btn obd-ai-catalog-list__icon-btn--variants"
                                                    onClick={() => {
                                                        if (onOpenCategoryLayoutOptions) {
                                                            void onOpenCategoryLayoutOptions({
                                                                categoryLabel: row.label,
                                                                layoutKey: row.key,
                                                                designLabel: row.label,
                                                                useSubpanel: useCatalogInlineSubpanels,
                                                            });
                                                            return;
                                                        }
                                                        setAltLayoutsCategory(row.label);
                                                        setAltLayoutsLoading(false);
                                                        setAltLayoutsModalOpen(true);
                                                    }}
                                                    title="Show layout variations"
                                                    aria-label="Show layout variations"
                                                >
                                                    <SquaresFourIcon size={16} weight="duotone" />
                                                </button>
                                            </div>
                                        ) : (
                                            <div className="obd-ai-catalog-list__actions obd-ai-catalog-list__actions--dashboard-grid">
                                                <button
                                                    type="button"
                                                    className="obd-ai-slide-designs__3d-btn obd-ai-catalog-list__icon-btn obd-ai-catalog-list__icon-btn--pricing"
                                                    disabled={isOverUsage || pricingBusy}
                                                    title="Load financing pricing"
                                                    aria-label={`Load financing pricing for ${row.label}`}
                                                    onClick={() => {
                                                        handlePricingDollarClick(row.key, {
                                                            estProd,
                                                            estModules,
                                                            systemKw: typeof systemKw === 'string' ? systemKw : String(systemKw ?? '0'),
                                                            systemSizeKw: systemSizeKwValue,
                                                            roofCount: rowRoofCount,
                                                            categoryId: row.label,
                                                            designId: cardKey,
                                                        });
                                                    }}
                                                >
                                                    {pricingBusy ? (
                                                        <Spin size="small" style={{ display: 'block' }} />
                                                    ) : (
                                                        <CurrencyDollarIcon size={16} weight="duotone" aria-hidden />
                                                    )}
                                                </button>
                                                <button
                                                    type="button"
                                                    className="obd-ai-slide-designs__3d-btn obd-ai-catalog-list__icon-btn obd-ai-catalog-list__icon-btn--open3d"
                                                    title="Open in 3D"
                                                    aria-label={`Open ${row.label} in 3D`}
                                                    onClick={() => {
                                                        void handleApplyLayoutClick(
                                                            row.key === 'default' ? 'default' : row.key,
                                                            row.key === 'default' ? undefined : row.label,
                                                        );
                                                    }}
                                                >
                                                    <CubeIcon size={16} weight="duotone" aria-hidden />
                                                </button>
                                                <button
                                                    type="button"
                                                    className="obd-ai-slide-designs__3d-btn obd-ai-catalog-list__icon-btn obd-ai-catalog-list__icon-btn--tree"
                                                    title="Tree cutting for this design"
                                                    aria-label="Tree cutting"
                                                    onClick={() => {
                                                        const cached = (treeScenariosByLayoutKey?.[row.key]?.length ?? 0) > 0;
                                                        const snap = thumbSnapshot;
                                                        if (useCatalogInlineSubpanels && onCatalogTreeSubpanelOpen) {
                                                            onCatalogTreeSubpanelOpen({
                                                                layoutKey: row.key,
                                                                designLabel: row.label,
                                                                mainSnapshotUrl: snap,
                                                                needsConfirm: !cached,
                                                            });
                                                            return;
                                                        }
                                                        if (cached) {
                                                            setSelectedRoofCombinationKey(row.key);
                                                            setTreeCuttingForLayoutKey(row.key);
                                                            setTreeCuttingPopupOpen(true);
                                                        } else {
                                                            setTreeCuttingConfirmForKey(row.key);
                                                            setTreeCuttingConfirmOpen(true);
                                                        }
                                                    }}
                                                >
                                                    <TreeEvergreenIcon size={16} weight="duotone" aria-hidden />
                                                </button>
                                                <button
                                                    type="button"
                                                    className={`obd-ai-slide-designs__3d-btn obd-ai-catalog-list__icon-btn obd-ai-catalog-list__icon-btn--wishlist${
                                                        wishlisted ? ' obd-ai-catalog-list__icon-btn--saved' : ''
                                                    }`}
                                                    onClick={() =>
                                                        handleWishlistDesign(
                                                            row.key,
                                                            row.label,
                                                            systemSizeKwValue ?? null,
                                                            rowRoofCount ?? null,
                                                        )
                                                    }
                                                    title={wishlisted ? 'Remove from wishlist' : 'Add to wishlist'}
                                                    aria-label={wishlisted ? 'Remove from wishlist' : 'Add to wishlist'}
                                                >
                                                    {wishlisted ? (
                                                        <HeartIcon size={16} weight="fill" />
                                                    ) : (
                                                        <HeartIcon size={16} weight="duotone" />
                                                    )}
                                                </button>
                                                <button
                                                    type="button"
                                                    className="obd-ai-slide-designs__3d-btn obd-ai-catalog-list__icon-btn obd-ai-catalog-list__icon-btn--variants"
                                                    onClick={() => {
                                                        const snap = thumbSnapshot;
                                                        if (onOpenCategoryLayoutOptions) {
                                                            void onOpenCategoryLayoutOptions({
                                                                categoryLabel: row.label,
                                                                layoutKey: row.key,
                                                                designLabel: row.label,
                                                                mainSnapshotUrl: snap,
                                                                useSubpanel: useCatalogInlineSubpanels,
                                                            });
                                                            return;
                                                        }
                                                        setAltLayoutsCategory(row.label);
                                                        setAltLayoutsLoading(false);
                                                        setAltLayoutsModalOpen(true);
                                                    }}
                                                    title="Show layout variations"
                                                    aria-label="Show layout variations"
                                                >
                                                    <SquaresFourIcon size={16} weight="duotone" />
                                                </button>
                                            </div>
                                        )}
                                    </CatalogGridCol>
                                </Row>
                            );
                        })}
                    </div>
                </div>
            </div>
            {snapshotPreview ? (
                <Modal
                    open
                    onCancel={() => setSnapshotPreview(null)}
                    footer={null}
                    centered
                    destroyOnHidden
                    width="min(96vw, 1120px)"
                    zIndex={10250}
                    title={snapshotPreview.label}
                    rootClassName={`obd-ai-snapshot-preview-modal obd-theme-${theme}`}
                    classNames={{ body: 'obd-ai-snapshot-preview-modal__body' }}
                >
                    <div className="obd-ai-snapshot-preview-modal__scroll">
                        <img
                            src={snapshotPreview.url}
                            alt={snapshotPreview.label}
                            className="obd-ai-snapshot-preview-modal__img"
                        />
                    </div>
                </Modal>
            ) : null}
        </div>
    );
};
